/**
 * AdminScreen — administración del directorio y glosario del tenant (Fase 32).
 *
 * Dos tabs, ambos con CRUD completo:
 *   - Directorio: personas del tenant. Lo consume el pipeline IA (P1.1
 *     matchea speakers contra el directorio, P3 resuelve menciones). Sin
 *     esta data, P1 termina pidiendo identificación manual de todos los
 *     speakers en cada meeting.
 *   - Glosario: términos técnicos del tenant. Lo consume P1.2 para corregir
 *     formas mal transcriptas por el ASR (ej. "cuber netis" → "Kubernetes").
 *     En v1 solo `category="global"`: el frontend hardcodea ese valor y
 *     nunca envía `clientId`, evitando la regla cross-field del backend.
 *
 * Patrones que reusamos de MeetingsScreen:
 *   - Confirmación inline al borrar (mismo "[¿Borrar?] [Confirmar] [Cancelar]").
 *   - Banner de error dismisable arriba del contenido.
 *   - Botones Button con $variant="primary" | "secondary" | "ghost".
 *   - Card como container de cada fila.
 *
 * Decisiones de Fase 32 (aplican a ambos tabs):
 *   - Ordenamiento: por addedAt desc (más nuevo arriba). ISO 8601 es
 *     lex-sortable, así que `localeCompare` directo sobre el string alcanza.
 *   - Arrays como input separado por comas. Directorio: `names` (≥1
 *     requerido). Glosario: `variants` (puede ser vacío).
 *   - Conflict 409: retry-once silencioso (log a consola + actualizar copia
 *     local desde el `current*` del body), banner amarillo solo si reincide.
 *   - Edición inline: la fila completa se reemplaza por el form mientras
 *     se edita. Sin popups, alineado con la filosofía del proyecto.
 *   - Agregar: form aparece arriba de la lista cuando se toca "+ Agregar".
 *   - PATCH vs ADD: ADD omite optionals vacíos para no pegarle a las
 *     validaciones del backend (regex de email para directorio, regla
 *     cross-field de category/clientId para glosario); PATCH siempre los
 *     manda (empty string = borrar, semántica documentada del wire). El
 *     campo `term` del glosario es excepción al patrón "empty borra" — el
 *     backend lo rechaza con 400, así que validamos client-side antes.
 *
 * Duplicación entre tabs (deliberada en v1):
 *   DirectoryTab y GlossaryTab comparten ~80% de estructura. Mismo patrón
 *   que `directory-storage.ts` vs `glossary-storage.ts` en el backend: la
 *   duplicación localiza cada lock a su entidad concreta y deja explícito
 *   qué se está protegiendo. Si en el futuro se agrega un tercer archivo
 *   en `_shared/` revisamos si vale abstraer; por ahora se mantienen
 *   espejados a propósito.
 */

import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import styled from "styled-components";

import { adminTabAtom } from "../../state/atoms";
import {
  ApiError,
  addDirectoryPerson,
  addGlossaryTerm,
  deleteDirectoryPerson,
  deleteGlossaryTerm,
  fetchDirectory,
  fetchGlossary,
  updateDirectoryPerson,
  updateGlossaryTerm,
} from "../../platform/api";
import type {
  AddDirectoryPersonRequest,
  AddGlossaryTermRequest,
  Directory,
  DirectoryConflictResponse,
  Glossary,
  GlossaryConflictResponse,
  UpdateDirectoryPersonRequest,
  UpdateGlossaryTermRequest,
} from "../../platform/api";

import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Stack } from "../components/Stack";
import { useNavigateTo, useSyncParams } from "../routing";

// Mismo regex que el backend (`handlers/directory.ts`). No es RFC 5322 completo;
// solo atrapa typos obvios. La validación real ocurre cuando se intenta enviar
// el email (Fase 64+).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Límites del backend. El handler los chequea y devuelve 400 si se exceden;
// los duplicamos client-side para evitar el round-trip y dar feedback inmediato.
const TERM_MAX_LEN = 200;
const VARIANT_MAX_LEN = 200;
const NOTES_MAX_LEN = 500;

// EditMode es compartido entre tabs porque tiene el mismo shape en ambos.
// Las dos pantallas mantienen instancias separadas; no hay estado global.
type EditMode =
  | { kind: "none" }
  | { kind: "add" }
  | { kind: "edit"; id: string };

export function AdminScreen() {
  const navigateTo = useNavigateTo();
  useSyncParams();
  const tab = useAtomValue(adminTabAtom);

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigateTo.meetings()}>← Volver</BackButton>
        <Title>Admin</Title>
      </Header>

      <Tabs role="tablist">
        <TabButton
          role="tab"
          aria-selected={tab === "directory"}
          $active={tab === "directory"}
          onClick={() => navigateTo.admin("directory")}
        >
          Directorio
        </TabButton>
        <TabButton
          role="tab"
          aria-selected={tab === "glossary"}
          $active={tab === "glossary"}
          onClick={() => navigateTo.admin("glossary")}
        >
          Glosario
        </TabButton>
      </Tabs>

      {tab === "directory" ? <DirectoryTab /> : <GlossaryTab />}
    </Container>
  );
}

// ===========================================================================
// Tab Directorio
// ===========================================================================

type PersonFormValues = {
  names: string;
  cargo: string;
  empresa: string;
  email: string;
};

const EMPTY_PERSON_FORM: PersonFormValues = {
  names: "",
  cargo: "",
  empresa: "",
  email: "",
};

function DirectoryTab() {
  const [directory, setDirectory] = useState<Directory | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState<EditMode>({ kind: "none" });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  // Aviso visible cuando el retry-once falla — significa que otro tab
  // está editando agresivamente y conviene que el usuario verifique
  // el estado actual antes de seguir.
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);

  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Initial fetch — useEffect es excepción legítima (network call, ver convenciones).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchDirectory()
      .then((d) => {
        if (!cancelled) setDirectory(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(formatError(err, "No se pudo cargar el directorio."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Personas ordenadas por addedAt desc (más nuevo arriba).
  const sortedPeople = useMemo(() => {
    if (!directory) return [];
    return [...directory.people].sort((a, b) =>
      b.addedAt.localeCompare(a.addedAt)
    );
  }, [directory]);

  // Helper que envuelve una mutación con retry-once en caso de 409.
  // Si el primer intento falla con 409, actualiza el directorio local
  // desde `currentDirectory` y reintenta. Si el reintento también es 409,
  // actualiza otra vez y propaga el error para que el caller muestre banner.
  async function attemptWithRetry<T>(mutate: () => Promise<T>): Promise<T> {
    try {
      return await mutate();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body) {
        const conflict = err.body as DirectoryConflictResponse;
        console.warn(
          "[Directory] 409 conflict, refreshing local state and retrying once",
          conflict
        );
        setDirectory(conflict.currentDirectory);
        // Retry una sola vez. Si vuelve a 409 propaga al caller.
        return await mutate();
      }
      throw err;
    }
  }

  // Caza específica de "retry-once también falló" para diferenciarlo de
  // otros errores. Lo usan los handlers de add/edit/delete para mostrar
  // el banner amarillo de aviso vs el banner rojo de error genérico.
  function handleMutationError(err: unknown) {
    if (err instanceof ApiError && err.status === 409 && err.body) {
      const conflict = err.body as DirectoryConflictResponse;
      setDirectory(conflict.currentDirectory);
      setConflictNotice(
        "Otro tab modificó el directorio mientras editabas. " +
          "Refrescamos los datos. Volvé a intentar."
      );
    } else {
      setMutationError(formatError(err, "La operación falló."));
    }
  }

  // -------- Handlers de mutación --------

  const handleAdd = async (values: PersonFormValues) => {
    const body = buildAddPersonBody(values);
    setSubmitting(true);
    setMutationError(null);
    setConflictNotice(null);
    try {
      const created = await attemptWithRetry(() => addDirectoryPerson(body));
      setDirectory((prev) =>
        prev
          ? {
              ...prev,
              people: [...prev.people, created],
              version: prev.version + 1,
            }
          : prev
      );
      setEditMode({ kind: "none" });
    } catch (err) {
      handleMutationError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (id: string, values: PersonFormValues) => {
    const body = buildUpdatePersonBody(values);
    setSubmitting(true);
    setMutationError(null);
    setConflictNotice(null);
    try {
      const updated = await attemptWithRetry(() =>
        updateDirectoryPerson(id, body)
      );
      setDirectory((prev) =>
        prev
          ? {
              ...prev,
              people: prev.people.map((p) => (p.id === id ? updated : p)),
              version: prev.version + 1,
            }
          : prev
      );
      setEditMode({ kind: "none" });
    } catch (err) {
      handleMutationError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async (id: string) => {
    setConfirmingDeleteId(null);
    setDeletingId(id);
    setMutationError(null);
    setConflictNotice(null);
    try {
      await attemptWithRetry(() => deleteDirectoryPerson(id));
      setDirectory((prev) =>
        prev
          ? {
              ...prev,
              people: prev.people.filter((p) => p.id !== id),
              version: prev.version + 1,
            }
          : prev
      );
    } catch (err) {
      handleMutationError(err);
    } finally {
      setDeletingId(null);
    }
  };

  // Retry del fetch inicial cuando el banner de error de carga muestra "Reintentar".
  const retryFetch = () => {
    setLoading(true);
    setLoadError(null);
    fetchDirectory()
      .then((d) => setDirectory(d))
      .catch((err: unknown) =>
        setLoadError(formatError(err, "No se pudo cargar el directorio."))
      )
      .finally(() => setLoading(false));
  };

  // -------- Render --------

  if (loading) {
    return <Status>Cargando directorio…</Status>;
  }

  if (loadError) {
    return (
      <ErrorBox>
        <p>{loadError}</p>
        <Button $variant="secondary" onClick={retryFetch}>
          Reintentar
        </Button>
      </ErrorBox>
    );
  }

  if (!directory) {
    return <Status>Sin datos.</Status>;
  }

  return (
    <Stack $direction="column" $gap="md">
      {mutationError && (
        <ErrorBanner $variant="error">
          <span>{mutationError}</span>
          <Button $variant="ghost" onClick={() => setMutationError(null)}>
            ✕
          </Button>
        </ErrorBanner>
      )}

      {conflictNotice && (
        <ErrorBanner $variant="warning">
          <span>{conflictNotice}</span>
          <Button $variant="ghost" onClick={() => setConflictNotice(null)}>
            ✕
          </Button>
        </ErrorBanner>
      )}

      <ListHeader>
        <Counter>
          {directory.people.length}{" "}
          {directory.people.length === 1 ? "persona" : "personas"}
        </Counter>
        {editMode.kind === "add" ? (
          <Button
            $variant="ghost"
            onClick={() => setEditMode({ kind: "none" })}
            disabled={submitting}
          >
            Cancelar
          </Button>
        ) : (
          <Button
            $variant="primary"
            onClick={() => {
              setEditMode({ kind: "add" });
              setMutationError(null);
            }}
            disabled={editMode.kind === "edit"}
          >
            + Agregar persona
          </Button>
        )}
      </ListHeader>

      {editMode.kind === "add" && (
        <PersonForm
          submitting={submitting}
          onSubmit={(values) => void handleAdd(values)}
          onCancel={() => setEditMode({ kind: "none" })}
        />
      )}

      {directory.people.length === 0 && editMode.kind !== "add" && (
        <EmptyHint>
          Todavía no agregaste a nadie. El pipeline IA usa este directorio
          para identificar speakers y resolver menciones automáticamente.
        </EmptyHint>
      )}

      <Stack $direction="column" $gap="sm">
        {sortedPeople.map((person) => {
          const isEditing =
            editMode.kind === "edit" && editMode.id === person.id;
          const isConfirming = confirmingDeleteId === person.id;
          const isDeleting = deletingId === person.id;

          if (isEditing) {
            return (
              <PersonForm
                key={person.id}
                initial={{
                  names: person.names.join(", "),
                  cargo: person.cargo ?? "",
                  empresa: person.empresa ?? "",
                  email: person.email ?? "",
                }}
                submitting={submitting}
                onSubmit={(values) => void handleEdit(person.id, values)}
                onCancel={() => setEditMode({ kind: "none" })}
              />
            );
          }

          const canonical = person.names[0] ?? "(sin nombre)";
          const aliases = person.names.slice(1);

          return (
            <EntityRow key={person.id} $faded={isDeleting}>
              <EntityInfo>
                <EntityName>
                  <strong>{canonical}</strong>
                  {aliases.length > 0 && (
                    <EntityAlias> · {aliases.join(", ")}</EntityAlias>
                  )}
                </EntityName>
                {(person.cargo || person.empresa) && (
                  <EntityMeta>
                    {person.cargo}
                    {person.cargo && person.empresa && " · "}
                    {person.empresa}
                  </EntityMeta>
                )}
                {person.email && <EntityMeta>{person.email}</EntityMeta>}
              </EntityInfo>
              <EntityActions>
                {isDeleting ? (
                  <DeletingLabel>Borrando…</DeletingLabel>
                ) : isConfirming ? (
                  <ConfirmRow>
                    <ConfirmLabel>¿Borrar?</ConfirmLabel>
                    <Button
                      $variant="primary"
                      onClick={() => void handleDeleteConfirm(person.id)}
                    >
                      Confirmar
                    </Button>
                    <Button
                      $variant="ghost"
                      onClick={() => setConfirmingDeleteId(null)}
                    >
                      Cancelar
                    </Button>
                  </ConfirmRow>
                ) : (
                  <ActionButtonsRow>
                    <Button
                      $variant="ghost"
                      onClick={() => {
                        setEditMode({ kind: "edit", id: person.id });
                        setMutationError(null);
                      }}
                      disabled={
                        editMode.kind === "add" || editMode.kind === "edit"
                      }
                    >
                      Editar
                    </Button>
                    <Button
                      $variant="ghost"
                      onClick={() => setConfirmingDeleteId(person.id)}
                    >
                      Borrar
                    </Button>
                  </ActionButtonsRow>
                )}
              </EntityActions>
            </EntityRow>
          );
        })}
      </Stack>
    </Stack>
  );
}

// ===========================================================================
// Tab Glosario
// ===========================================================================

type TermFormValues = {
  term: string;
  variants: string;
  notes: string;
};

const EMPTY_TERM_FORM: TermFormValues = {
  term: "",
  variants: "",
  notes: "",
};

function GlossaryTab() {
  const [glossary, setGlossary] = useState<Glossary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState<EditMode>({ kind: "none" });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);

  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchGlossary()
      .then((g) => {
        if (!cancelled) setGlossary(g);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(formatError(err, "No se pudo cargar el glosario."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedTerms = useMemo(() => {
    if (!glossary) return [];
    return [...glossary.terms].sort((a, b) =>
      b.addedAt.localeCompare(a.addedAt)
    );
  }, [glossary]);

  // Análogo del attemptWithRetry de DirectoryTab pero cerrando sobre setGlossary
  // y casting el body al tipo correcto de conflict response.
  async function attemptWithRetry<T>(mutate: () => Promise<T>): Promise<T> {
    try {
      return await mutate();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.body) {
        const conflict = err.body as GlossaryConflictResponse;
        console.warn(
          "[Glossary] 409 conflict, refreshing local state and retrying once",
          conflict
        );
        setGlossary(conflict.currentGlossary);
        return await mutate();
      }
      throw err;
    }
  }

  function handleMutationError(err: unknown) {
    if (err instanceof ApiError && err.status === 409 && err.body) {
      const conflict = err.body as GlossaryConflictResponse;
      setGlossary(conflict.currentGlossary);
      setConflictNotice(
        "Otro tab modificó el glosario mientras editabas. " +
          "Refrescamos los datos. Volvé a intentar."
      );
    } else {
      setMutationError(formatError(err, "La operación falló."));
    }
  }

  // -------- Handlers de mutación --------

  const handleAdd = async (values: TermFormValues) => {
    const body = buildAddTermBody(values);
    setSubmitting(true);
    setMutationError(null);
    setConflictNotice(null);
    try {
      const created = await attemptWithRetry(() => addGlossaryTerm(body));
      setGlossary((prev) =>
        prev
          ? {
              ...prev,
              terms: [...prev.terms, created],
              version: prev.version + 1,
            }
          : prev
      );
      setEditMode({ kind: "none" });
    } catch (err) {
      handleMutationError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (id: string, values: TermFormValues) => {
    const body = buildUpdateTermBody(values);
    setSubmitting(true);
    setMutationError(null);
    setConflictNotice(null);
    try {
      const updated = await attemptWithRetry(() =>
        updateGlossaryTerm(id, body)
      );
      setGlossary((prev) =>
        prev
          ? {
              ...prev,
              terms: prev.terms.map((t) => (t.id === id ? updated : t)),
              version: prev.version + 1,
            }
          : prev
      );
      setEditMode({ kind: "none" });
    } catch (err) {
      handleMutationError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async (id: string) => {
    setConfirmingDeleteId(null);
    setDeletingId(id);
    setMutationError(null);
    setConflictNotice(null);
    try {
      await attemptWithRetry(() => deleteGlossaryTerm(id));
      setGlossary((prev) =>
        prev
          ? {
              ...prev,
              terms: prev.terms.filter((t) => t.id !== id),
              version: prev.version + 1,
            }
          : prev
      );
    } catch (err) {
      handleMutationError(err);
    } finally {
      setDeletingId(null);
    }
  };

  const retryFetch = () => {
    setLoading(true);
    setLoadError(null);
    fetchGlossary()
      .then((g) => setGlossary(g))
      .catch((err: unknown) =>
        setLoadError(formatError(err, "No se pudo cargar el glosario."))
      )
      .finally(() => setLoading(false));
  };

  // -------- Render --------

  if (loading) {
    return <Status>Cargando glosario…</Status>;
  }

  if (loadError) {
    return (
      <ErrorBox>
        <p>{loadError}</p>
        <Button $variant="secondary" onClick={retryFetch}>
          Reintentar
        </Button>
      </ErrorBox>
    );
  }

  if (!glossary) {
    return <Status>Sin datos.</Status>;
  }

  return (
    <Stack $direction="column" $gap="md">
      {mutationError && (
        <ErrorBanner $variant="error">
          <span>{mutationError}</span>
          <Button $variant="ghost" onClick={() => setMutationError(null)}>
            ✕
          </Button>
        </ErrorBanner>
      )}

      {conflictNotice && (
        <ErrorBanner $variant="warning">
          <span>{conflictNotice}</span>
          <Button $variant="ghost" onClick={() => setConflictNotice(null)}>
            ✕
          </Button>
        </ErrorBanner>
      )}

      <ListHeader>
        <Counter>
          {glossary.terms.length}{" "}
          {glossary.terms.length === 1 ? "término" : "términos"}
        </Counter>
        {editMode.kind === "add" ? (
          <Button
            $variant="ghost"
            onClick={() => setEditMode({ kind: "none" })}
            disabled={submitting}
          >
            Cancelar
          </Button>
        ) : (
          <Button
            $variant="primary"
            onClick={() => {
              setEditMode({ kind: "add" });
              setMutationError(null);
            }}
            disabled={editMode.kind === "edit"}
          >
            + Agregar término
          </Button>
        )}
      </ListHeader>

      {editMode.kind === "add" && (
        <TermForm
          submitting={submitting}
          onSubmit={(values) => void handleAdd(values)}
          onCancel={() => setEditMode({ kind: "none" })}
        />
      )}

      {glossary.terms.length === 0 && editMode.kind !== "add" && (
        <EmptyHint>
          Todavía no agregaste términos. El pipeline IA usa este glosario
          para corregir transcripciones erradas del ASR (ej. "cuber netis"
          → "Kubernetes").
        </EmptyHint>
      )}

      <Stack $direction="column" $gap="sm">
        {sortedTerms.map((term) => {
          const isEditing =
            editMode.kind === "edit" && editMode.id === term.id;
          const isConfirming = confirmingDeleteId === term.id;
          const isDeleting = deletingId === term.id;

          if (isEditing) {
            return (
              <TermForm
                key={term.id}
                initial={{
                  term: term.term,
                  variants: term.variants.join(", "),
                  notes: term.notes ?? "",
                }}
                submitting={submitting}
                onSubmit={(values) => void handleEdit(term.id, values)}
                onCancel={() => setEditMode({ kind: "none" })}
              />
            );
          }

          return (
            <EntityRow key={term.id} $faded={isDeleting}>
              <EntityInfo>
                <EntityName>
                  <strong>{term.term}</strong>
                </EntityName>
                {term.variants.length > 0 && (
                  <EntityMeta>
                    Variantes: {term.variants.join(", ")}
                  </EntityMeta>
                )}
                {term.notes && <EntityMeta>{term.notes}</EntityMeta>}
              </EntityInfo>
              <EntityActions>
                {isDeleting ? (
                  <DeletingLabel>Borrando…</DeletingLabel>
                ) : isConfirming ? (
                  <ConfirmRow>
                    <ConfirmLabel>¿Borrar?</ConfirmLabel>
                    <Button
                      $variant="primary"
                      onClick={() => void handleDeleteConfirm(term.id)}
                    >
                      Confirmar
                    </Button>
                    <Button
                      $variant="ghost"
                      onClick={() => setConfirmingDeleteId(null)}
                    >
                      Cancelar
                    </Button>
                  </ConfirmRow>
                ) : (
                  <ActionButtonsRow>
                    <Button
                      $variant="ghost"
                      onClick={() => {
                        setEditMode({ kind: "edit", id: term.id });
                        setMutationError(null);
                      }}
                      disabled={
                        editMode.kind === "add" || editMode.kind === "edit"
                      }
                    >
                      Editar
                    </Button>
                    <Button
                      $variant="ghost"
                      onClick={() => setConfirmingDeleteId(term.id)}
                    >
                      Borrar
                    </Button>
                  </ActionButtonsRow>
                )}
              </EntityActions>
            </EntityRow>
          );
        })}
      </Stack>
    </Stack>
  );
}

// ===========================================================================
// PersonForm — form reutilizable para agregar y editar personas
// ===========================================================================

type PersonFormProps = {
  initial?: PersonFormValues;
  submitting: boolean;
  onSubmit: (values: PersonFormValues) => void;
  onCancel: () => void;
};

function PersonForm({ initial, submitting, onSubmit, onCancel }: PersonFormProps) {
  const [values, setValues] = useState<PersonFormValues>(
    initial ?? EMPTY_PERSON_FORM
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    // Validación client-side antes de pegarle al backend. Los chequeos
    // duplican lo que valida el handler — el backend siempre tiene la
    // última palabra, pero acá evitamos round-trips por errores obvios.
    const parsedNames = parseCommaList(values.names);
    if (parsedNames.length === 0) {
      setError("Tenés que poner al menos un nombre.");
      return;
    }
    const email = values.email.trim();
    if (email.length > 0 && !EMAIL_REGEX.test(email)) {
      setError("Email con formato inválido.");
      return;
    }
    setError(null);
    onSubmit(values);
  };

  const update =
    (key: keyof PersonFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [key]: e.target.value }));
    };

  return (
    <FormCard>
      <FormField>
        <FormLabel>Nombre(s)</FormLabel>
        <FormInput
          type="text"
          placeholder="Juan, Juancho, Juani"
          value={values.names}
          onChange={update("names")}
          autoFocus
        />
        <FormHint>
          Separados por coma. El primero es el nombre canónico.
        </FormHint>
      </FormField>

      <FormField>
        <FormLabel>Cargo</FormLabel>
        <FormInput
          type="text"
          placeholder="CTO"
          value={values.cargo}
          onChange={update("cargo")}
        />
      </FormField>

      <FormField>
        <FormLabel>Empresa</FormLabel>
        <FormInput
          type="text"
          placeholder="Acme"
          value={values.empresa}
          onChange={update("empresa")}
        />
      </FormField>

      <FormField>
        <FormLabel>Email</FormLabel>
        <FormInput
          type="email"
          placeholder="juan@acme.com"
          value={values.email}
          onChange={update("email")}
        />
      </FormField>

      {error && <FormError>{error}</FormError>}

      <FormActions>
        <Button $variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Guardando…" : "Guardar"}
        </Button>
        <Button $variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
      </FormActions>
    </FormCard>
  );
}

// ===========================================================================
// TermForm — form reutilizable para agregar y editar términos del glosario
// ===========================================================================

type TermFormProps = {
  initial?: TermFormValues;
  submitting: boolean;
  onSubmit: (values: TermFormValues) => void;
  onCancel: () => void;
};

function TermForm({ initial, submitting, onSubmit, onCancel }: TermFormProps) {
  const [values, setValues] = useState<TermFormValues>(
    initial ?? EMPTY_TERM_FORM
  );
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    // `term` es obligatorio y no admite vacío (regla específica del backend
    // para glosario — distinto del directorio donde empty borra optionals).
    const term = values.term.trim();
    if (term.length === 0) {
      setError("Tenés que poner el término canónico.");
      return;
    }
    if (term.length > TERM_MAX_LEN) {
      setError(`El término no puede superar los ${TERM_MAX_LEN} caracteres.`);
      return;
    }
    // Validación de cada variante: no superan VARIANT_MAX_LEN chars.
    // parseCommaList ya filtra vacíos tras trim.
    const variants = parseCommaList(values.variants);
    const tooLongVariant = variants.find((v) => v.length > VARIANT_MAX_LEN);
    if (tooLongVariant) {
      setError(
        `Variante demasiado larga (${VARIANT_MAX_LEN} caracteres máximo): "${tooLongVariant.slice(0, 40)}…"`
      );
      return;
    }
    if (values.notes.length > NOTES_MAX_LEN) {
      setError(`Las notas no pueden superar los ${NOTES_MAX_LEN} caracteres.`);
      return;
    }
    setError(null);
    onSubmit(values);
  };

  const updateInput =
    (key: "term" | "variants") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const updateTextarea =
    (key: "notes") => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValues((prev) => ({ ...prev, [key]: e.target.value }));
    };

  return (
    <FormCard>
      <FormField>
        <FormLabel>Término</FormLabel>
        <FormInput
          type="text"
          placeholder="Kubernetes"
          value={values.term}
          onChange={updateInput("term")}
          autoFocus
        />
        <FormHint>Forma canónica. La que el ASR debería haber producido.</FormHint>
      </FormField>

      <FormField>
        <FormLabel>Variantes</FormLabel>
        <FormInput
          type="text"
          placeholder="cuber netis, cubernetis, kubernet"
          value={values.variants}
          onChange={updateInput("variants")}
        />
        <FormHint>
          Separadas por coma. Formas mal transcriptas observadas que la IA
          va a reemplazar por el término canónico. Podés dejarlo vacío.
        </FormHint>
      </FormField>

      <FormField>
        <FormLabel>Notas</FormLabel>
        <FormTextarea
          placeholder="Contexto opcional sobre el término…"
          value={values.notes}
          onChange={updateTextarea("notes")}
          rows={3}
          maxLength={NOTES_MAX_LEN}
        />
        <FormHint>
          {values.notes.length}/{NOTES_MAX_LEN}. La IA no las lee — son
          referencia para tu equipo.
        </FormHint>
      </FormField>

      {error && <FormError>{error}</FormError>}

      <FormActions>
        <Button $variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Guardando…" : "Guardar"}
        </Button>
        <Button $variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
      </FormActions>
    </FormCard>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

// Parser compartido para listas separadas por coma con trim y filter de vacíos.
// Lo usan tanto `names` (directorio) como `variants` (glosario).
function parseCommaList(input: string): string[] {
  return input
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// ADD: solo incluye optionals si tienen valor. Mandar `email: ""` haría
// fallar el regex del backend. Para los demás optionals seguimos la misma
// regla por consistencia — menos chance de side effects raros.
function buildAddPersonBody(values: PersonFormValues): AddDirectoryPersonRequest {
  const body: AddDirectoryPersonRequest = {
    names: parseCommaList(values.names),
  };
  const cargo = values.cargo.trim();
  const empresa = values.empresa.trim();
  const email = values.email.trim();
  if (cargo) body.cargo = cargo;
  if (empresa) body.empresa = empresa;
  if (email) body.email = email;
  return body;
}

// PATCH: mandamos siempre todos los campos. Empty string en optionals
// dispara la semántica de borrado documentada del backend. `names`
// siempre va con el array parseado (el backend exige no-vacío).
function buildUpdatePersonBody(
  values: PersonFormValues
): UpdateDirectoryPersonRequest {
  return {
    names: parseCommaList(values.names),
    cargo: values.cargo.trim(),
    empresa: values.empresa.trim(),
    email: values.email.trim(),
  };
}

// ADD glosario: `term` y `variants` son requeridos (variants puede ser []).
// `category` hardcoded a "global" (decisión de Fase 31). `clientId` nunca
// se envía. `notes` solo si tiene valor — el backend acepta empty pero
// guardamos el archivo más prolijo.
function buildAddTermBody(values: TermFormValues): AddGlossaryTermRequest {
  const body: AddGlossaryTermRequest = {
    term: values.term.trim(),
    variants: parseCommaList(values.variants),
    category: "global",
  };
  const notes = values.notes.trim();
  if (notes) body.notes = notes;
  return body;
}

// PATCH glosario: `term` siempre se manda (no admite empty — la validación
// client-side garantiza que no llegue vacío). `variants` siempre — empty
// array reemplaza wholesale. `notes` siempre — empty string borra el campo
// (semántica documentada del backend). `category` y `clientId` no se tocan
// desde la UI en v1 (todos los términos son "global", sin client).
function buildUpdateTermBody(
  values: TermFormValues
): UpdateGlossaryTermRequest {
  return {
    term: values.term.trim(),
    variants: parseCommaList(values.variants),
    notes: values.notes.trim(),
  };
}

function formatError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return err.message || fallback;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  return fallback;
}

// ===========================================================================
// Styled components
//
// Los nombres `Entity*` (Row, Info, Name, Alias, Meta, Actions) son
// genéricos a propósito: los usan tanto las filas del directorio como las
// del glosario porque el shape visual es idéntico (header bold + meta lines
// + acciones a la derecha). Los nombres `Form*` también son compartidos
// entre los dos forms con la única adición de FormTextarea para el campo
// notes del glosario.
// ===========================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.textPrimary};
  cursor: pointer;
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  font-size: ${({ theme }) => theme.font.size.md};

  &:hover {
    color: ${({ theme }) => theme.color.accent};
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.font.size.xxl};
  font-weight: ${({ theme }) => theme.font.weight.bold};
`;

const Tabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.color.accentMuted : "transparent"};
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.color.accent : "transparent"};
  color: ${({ $active, theme }) =>
    $active ? theme.color.accent : theme.color.textMuted};
  font-weight: ${({ $active, theme }) =>
    $active ? theme.font.weight.bold : theme.font.weight.medium};
  font-size: ${({ theme }) => theme.font.size.md};
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.color.accent};
  }
`;

const Status = styled.div`
  padding: ${({ theme }) => theme.space.md};
  text-align: center;
  color: ${({ theme }) => theme.color.textMuted};
`;

const ErrorBox = styled(Card)`
  border-color: ${({ theme }) => theme.color.error};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
`;

const ErrorBanner = styled.div<{ $variant: "error" | "warning" }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  background: ${({ $variant, theme }) =>
    $variant === "error" ? theme.color.errorMuted : theme.color.warningMuted};
  color: ${({ $variant, theme }) =>
    $variant === "error" ? theme.color.error : theme.color.warning};
  border: 1px solid
    ${({ $variant, theme }) =>
      $variant === "error" ? theme.color.error : theme.color.warning};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.font.size.sm};
  gap: ${({ theme }) => theme.space.md};
`;

const ListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Counter = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const EmptyHint = styled(Card)`
  text-align: center;
  padding: ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.color.textMuted};
  font-size: ${({ theme }) => theme.font.size.sm};
`;

const EntityRow = styled(Card)<{ $faded?: boolean }>`
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  opacity: ${({ $faded }) => ($faded ? 0.5 : 1)};
  transition: opacity 0.15s ease;
`;

const EntityInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
  min-width: 0;
`;

const EntityName = styled.div`
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const EntityAlias = styled.span`
  color: ${({ theme }) => theme.color.textMuted};
  font-weight: ${({ theme }) => theme.font.weight.normal};
`;

const EntityMeta = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const EntityActions = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const ActionButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
`;

const ConfirmRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
`;

const ConfirmLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.error};
  font-weight: ${({ theme }) => theme.font.weight.bold};
  white-space: nowrap;
`;

const DeletingLabel = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;

const FormCard = styled(Card)`
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
  border-color: ${({ theme }) => theme.color.accent};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const FormLabel = styled.label`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const FormInput = styled.input`
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.font.size.md};
  font-family: ${({ theme }) => theme.font.family};
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surface};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.accentMuted};
  }
`;

const FormTextarea = styled.textarea`
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.font.size.md};
  font-family: ${({ theme }) => theme.font.family};
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surface};
  resize: vertical;
  min-height: 60px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.accentMuted};
  }
`;

const FormHint = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textDim};
`;

const FormError = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.error};
`;

const FormActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  justify-content: flex-end;
`;
